/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import { v4 as uuid4 } from 'uuid';
import * as color from '@constant/color';
import * as size from '@constant/size';
import TextBlock from '@component/common/block/text-block';
import ImageAudioButton from '@design-system/image-audio-button';
import Scroll, { ScrollContainer } from '@component/common/scroll';
import Gap from '@component/common/gap';
import { ETheme } from '@datatype/type';
import { EApiSubCharacterType, IClassroomCharacterData } from '@datatype/iyagigo/api-type';
import CharacterStrokeBoard from '@component/iyagigo/classroom/character-study/character-stroke-board';

interface IProps {
  character: IClassroomCharacterData;
  subCharacterType?: EApiSubCharacterType | null;
}

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 10px; // box shadow때문에
`;

// 흰색 카드 (단어집 VocabularyCard 패턴 — 회색 배경 위 흰 카드 + 그림자, 영역 꽉 채움)
const Card = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: space-between;
  padding: ${size.PAGE_PADDING};
  border-radius: ${size.BORDER_RADIUS_2};
  background-color: ${color.WHITE_1};
  box-shadow: ${color.SURFACE_SHADOW};
`;

// 글자 영역 (카드 상단, 중앙 큰 글자)
const SymbolContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

// 큰 글자 + 우측 발음 스피커 아이콘
const CharacterContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const PronunciationContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: baseline;
  justify-content: center;
  gap: ${size.MARGIN_1};
`;

const PronunciationTextContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: baseline;
  gap: 3px;
`;

// 예시 단어 목록 (카드 안, 예제 박스 세로 나열) — 스크롤 영역 내부
// margin-top: auto — 예시가 적으면 하단으로 밀어 원래 space-between 느낌 유지, 많으면 스크롤
const ExamplesContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: stretch;
  width: 100%;
  gap: ${size.MARGIN_1};
  margin-top: auto;
`;

// 예시 단어 1개 (옅은 회색 박스)
const ExampleContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${size.MARGIN_1};
  border-radius: ${size.BORDER_RADIUS_1};
  background-color: ${color.GRAY_BACKGROUND_COLOR};
  //gap: ${size.MARGIN_1};
`;

const ExampleTextContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
`;

// 예시 단어 + 한글 읽기 (글자 영역과 동일하게 baseline row)
const ExampleVocabularyContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: baseline;
  gap: 5px;
`;

const CharacterCard: React.FC<IProps> = ({ character, subCharacterType }) => {
  const mainChar = character.symbol;

  // 작게 쓰는 글자(촉음·요음)는 단독 발음을 들려주지 않음 — 발음 버튼 숨김 (audioLink는 저장됨, 노출은 UI 책임)
  const isSmallKana =
    subCharacterType === EApiSubCharacterType.SOKUON || subCharacterType === EApiSubCharacterType.YOON;

  const Examples = character.example.map((example) => (
    <ExampleContainer key={uuid4()}>
      <ExampleTextContainer>
        <ExampleVocabularyContainer>
          <TextBlock
            text={example.vocabulary}
            design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
          />
          {example.pronunciation?.ko && (
            <TextBlock
              text={`[${example.pronunciation.ko}]`}
              design={{ text: { size: 'small', color: color.GRAY_1, bold: true }, box: { alignX: 'left' } }}
            />
          )}
        </ExampleVocabularyContainer>
        <TextBlock
          text={example.meaning.ko ?? ''}
          design={{ text: { size: 'medium', color: color.GRAY_1 }, box: { alignX: 'left' } }}
        />
      </ExampleTextContainer>
      {example.audioLink && (
        <ImageAudioButton
          sources={[example.audioLink]}
          theme={ETheme.GRAY}
          tone='normal'
          kind='outlined'
          size='small'
          shape='round'
          width='35px'
        />
      )}
    </ExampleContainer>
  ));

  return (
    <Wrapper>
      <Card>
        <SymbolContainer>
          {/*<Gap height={size.MARGIN_4} />*/}
          <CharacterContainer>
            <CharacterStrokeBoard character={mainChar} source='animcjk' />
            <Gap height={size.MARGIN_1} />
            {/* 촉음(っ)의 ko='받침'은 발음이 아닌 설명이라 병기하지 않음 (description이 대신함) */}
            {!isSmallKana && (
              <PronunciationContainer>
                <PronunciationTextContainer>
                  <TextBlock
                    text={`${character.pronunciation.ko}`}
                    design={{ text: { size: 'big', color: color.BROWN_1, bold: true }, box: {} }}
                  />
                  <TextBlock text='로 읽어요' design={{ text: { size: 'large', color: color.BROWN_2 }, box: {} }} />
                </PronunciationTextContainer>
                {character.audioLink && (
                  <ImageAudioButton
                    sources={[character.audioLink]}
                    theme={ETheme.BROWN}
                    tone='normal'
                    kind='outlined'
                    size='medium'
                    shape='round'
                    height='30px'
                    width='45px'
                  />
                )}
              </PronunciationContainer>
            )}
          </CharacterContainer>
          <Gap height={'5px'} />
          {/* 글자 설명 — 탁음/촉음/요음 등 개념 설명 (촉음 っ은 발음 버튼 없이 설명이 본문) */}
          {character.description && (
            <>
              {/*<Gap height={size.MARGIN_1} />*/}
              <TextBlock
                text={character.description}
                design={{ text: { size: 'medium', color: color.GRAY_1 }, box: {} }}
              />
            </>
          )}
        </SymbolContainer>
        <Gap height={size.MARGIN_1} />
        {/* 글자(SymbolContainer)는 고정, 예시 단어는 남은 영역에서 스크롤 */}
        <ScrollContainer width={'100%'}>
          <Scroll direction='y' height='100%'>
            <ExamplesContainer>{Examples}</ExamplesContainer>
          </Scroll>
        </ScrollContainer>
      </Card>
    </Wrapper>
  );
};

export default CharacterCard;
